import React, { useEffect, useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { assignmentsApi, coursesApi, orgApi, resultsApi } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import { errorBanner, formStyle, inputStyle, labelStyle, pageStyle, primaryButton } from '../formUi';

const AssignmentsPage: React.FC = () => {
  const { user } = useAuth();
  const [items, setItems] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [courseSearch, setCourseSearch] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [selectedCourseIds, setSelectedCourseIds] = useState<string[]>([]);
  const [resultFor, setResultFor] = useState<any>(null);
  const [score, setScore] = useState('');

  const load = async () => {
    try {
      const params: any = {};
      if (user?.role === 'Managers') params.createdBy = user.id;
      const [a, e, c] = await Promise.all([
        assignmentsApi.list(),
        orgApi.listEmployees(user?.role === 'Managers' ? { createdBy: user.id } : undefined),
        coursesApi.list(),
      ]);
      setItems(a);
      setEmployees(e);
      setCourses(c);
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message);
    }
  };

  useEffect(() => {
    load();
  }, [user?.id]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEmployeeId || selectedCourseIds.length === 0) {
      setError('Select an employee and at least one course.');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      for (const courseId of selectedCourseIds) {
        await assignmentsApi.create({ employeeId: selectedEmployeeId, courseId });
      }
      setCreating(false);
      setSelectedEmployeeId('');
      setSelectedCourseIds([]);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const submitResult = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await resultsApi.create({
        assignmentId: resultFor.id,
        score: Number(score),
        passed: Number(score) >= (resultFor.course?.passingScore || 70),
        answers: [],
      });
      setResultFor(null);
      setScore('');
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message);
    }
  };

  const displayEmployees = employees.filter((emp) => {
    const q = employeeSearch.trim().toLowerCase();
    if (!q) return true;
    return `${emp.name} ${emp.email} ${emp.department || ''}`.toLowerCase().includes(q);
  });
  const filteredCourses = courses.filter((c) => c.title?.toLowerCase().includes(courseSearch.trim().toLowerCase()));

  if (resultFor) {
    return (
      <div style={pageStyle(600)}>
        <h2>Record Quiz Result</h2>
        <form onSubmit={submitResult} style={formStyle}>
          {error && <div style={errorBanner}>{error}</div>}
          <p><strong>Employee:</strong> {resultFor.employee?.name}</p>
          <p><strong>Course:</strong> {resultFor.course?.title}</p>
          <div>
            <label style={labelStyle}>Score (%)</label>
            <input required type="number" min={0} max={100} value={score} onChange={(e) => setScore(e.target.value)} style={{ ...inputStyle, width: 120 }} />
          </div>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => setResultFor(null)} style={{ padding: '0.75rem 1.5rem', background: '#f5f5f5', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}>Cancel</button>
            <button type="submit" style={primaryButton(false)}>Save Result</button>
          </div>
        </form>
      </div>
    );
  }

  if (creating) {
    const selectedEmployee = employees.find((emp) => emp.id === selectedEmployeeId);
    return (
      <div style={pageStyle(800)}>
        <h2>Assign Courses to Employee</h2>
        <form onSubmit={create} style={{ ...formStyle, gap: '2rem' }}>
          {error && <div style={errorBanner}>{error}</div>}
          <div>
            <label style={labelStyle}>Select Employee *</label>
            {employees.length === 0 ? (
              <p style={{ color: '#d32f2f', fontStyle: 'italic' }}>No employees found. You'll need to add employees first.</p>
            ) : (
              <div style={{ border: '1px solid #ccc', borderRadius: 4, padding: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: '#555' }}>Search employees</label>
                <input type="search" value={employeeSearch} onChange={(e) => setEmployeeSearch(e.target.value)} placeholder="Type name, email, or department…" style={{ ...inputStyle, marginBottom: '0.75rem' }} />
                <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                  {displayEmployees.map((employee) => (
                    <div key={employee.id} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '0.75rem', padding: '0.75rem', border: '1px solid #e0e0e0', borderRadius: 4, background: selectedEmployeeId === employee.id ? '#f0f8ff' : 'white' }}>
                      <input type="radio" name="assign-employee" checked={selectedEmployeeId === employee.id} onChange={() => setSelectedEmployeeId(employee.id)} style={{ marginRight: '0.75rem', marginTop: '0.25rem' }} />
                      <label style={{ flex: 1, cursor: 'pointer' }} onClick={() => setSelectedEmployeeId(employee.id)}>
                        <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>{employee.name}</div>
                        <div style={{ fontSize: '0.9rem', color: '#666' }}>{employee.email}</div>
                        {employee.department && <div style={{ fontSize: '0.8rem', color: '#999' }}>{employee.department}</div>}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div>
            <label style={labelStyle}>Select Courses * (Select one or more)</label>
            {courses.length === 0 ? (
              <p style={{ color: '#d32f2f', fontStyle: 'italic' }}>No courses found. Create courses first before assigning them.</p>
            ) : (
              <div style={{ border: '1px solid #ccc', borderRadius: 4, padding: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: '#555' }}>Search courses</label>
                <input type="search" value={courseSearch} onChange={(e) => setCourseSearch(e.target.value)} placeholder="Type to filter by course title…" style={{ ...inputStyle, marginBottom: '0.75rem' }} />
                <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                  {filteredCourses.map((course) => (
                    <div key={course.id} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '1rem', padding: '0.75rem', border: '1px solid #e0e0e0', borderRadius: 4, background: selectedCourseIds.includes(course.id) ? '#f0f8ff' : 'white' }}>
                      <input
                        type="checkbox"
                        checked={selectedCourseIds.includes(course.id)}
                        onChange={(e) => setSelectedCourseIds((ids) => e.target.checked ? [...ids, course.id] : ids.filter((id) => id !== course.id))}
                        style={{ marginRight: '0.75rem', marginTop: '0.25rem' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>{course.title}</div>
                        <div style={{ fontSize: '0.9rem', color: '#666' }}>Passing Score: {course.passingScore ?? 'Not set'}%</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {selectedCourseIds.length > 0 && <p style={{ marginTop: '0.5rem', color: '#1976d2', fontSize: '0.9rem' }}>{selectedCourseIds.length} course(s) selected</p>}
          </div>
          <button type="submit" disabled={submitting || !employees.length || !courses.length} style={{ ...primaryButton(submitting || !employees.length || !courses.length), padding: '1rem 2rem', fontSize: '1.1rem' }}>
            {submitting ? 'Creating Assignments...' : 'Assign Courses'}
          </button>
          <button type="button" onClick={() => { setCreating(false); setError(null); }} style={{ background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer' }}>
            Cancel
          </button>
        </form>
        {selectedEmployee && selectedCourseIds.length > 0 && (
          <div style={{ marginTop: '2rem', padding: '1rem', background: '#f0f8ff', borderRadius: 4, border: '1px solid #1976d2' }}>
            <h4 style={{ margin: '0 0 0.5rem', color: '#1976d2' }}>Assignment Summary</h4>
            <p style={{ margin: '0.25rem 0' }}><strong>Employee:</strong> {selectedEmployee.name}</p>
            <p style={{ margin: '0.25rem 0' }}><strong>Courses:</strong> {selectedCourseIds.length} selected</p>
            <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
              {selectedCourseIds.map((id) => (
                <li key={id}>{courses.find((c) => c.id === id)?.title}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  return (
    <Box>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h3 style={{ margin: 0 }}>Assignments ({items.length})</h3>
        <button type="button" onClick={() => setCreating(true)} style={{ padding: '0.5rem 1rem', backgroundColor: '#1976d2', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
          + Assign Course
        </button>
      </div>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {items.map((a) => (
        <Box key={a.id} sx={{ p: '1.5rem', mb: 2, border: '1px solid #e0e0e0', borderRadius: '8px', bgcolor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <Typography sx={{ color: '#1976d2', fontWeight: 700 }}>
            {a.employee?.name} → {a.course?.title}
          </Typography>
          <Typography variant="body2">
            Status: {a.status} · Complete: {a.isTrainingComplete ? 'yes' : 'no'} · Results: {a.results?.length || 0}
          </Typography>
          <button type="button" onClick={() => { setResultFor(a); setScore(String(a.course?.passingScore || 80)); }} style={{ marginTop: '0.75rem', padding: '0.4rem 0.8rem', background: '#f5f5f5', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}>
            Record quiz result
          </button>
        </Box>
      ))}
    </Box>
  );
};

export default AssignmentsPage;
